import {
  Anchor,
  Button,
  Card,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { AdminShell } from '../../components/admin-shell/AdminShell';
import { adminGet, adminPost } from './_api';

type Letter = {
  id: string;
  slug: string;
  label: string;
  title: string;
  excerpt: string;
  body: string;
  author: string;
  audience: 'all' | 'artists' | 'hosts' | 'founding_cohort';
  published_at: string;
};

export const Route = createFileRoute('/admin/announcements')({
  component: AdminAnnouncements,
});

function AdminAnnouncements() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'announcements'],
    queryFn: () => adminGet<{ items: Letter[] }>('/api/admin/announcements'),
  });

  const [slug, setSlug] = useState('');
  const [label, setLabel] = useState('');
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('Richie & the WWAAVE team');
  const [audience, setAudience] = useState<string>('all');
  const [excerpt, setExcerpt] = useState('');
  const [body, setBody] = useState('');

  const publish = useMutation({
    mutationFn: () =>
      adminPost('/api/admin/announcements', {
        slug,
        label,
        title,
        excerpt,
        body,
        author,
        audience,
      }),
    onSuccess: () => {
      setSlug('');
      setLabel('');
      setTitle('');
      setExcerpt('');
      setBody('');
      void qc.invalidateQueries({ queryKey: ['admin', 'announcements'] });
    },
  });

  const canPublish =
    slug.trim().length >= 2 &&
    label.trim().length >= 2 &&
    title.trim().length >= 2 &&
    excerpt.trim().length >= 20 &&
    body.trim().length >= 40 &&
    author.trim().length >= 2;

  return (
    <AdminShell>
      <Stack gap="lg">
        <Title order={2}>Announcements</Title>

        <Card withBorder padding="md">
          <Title order={4} mb="md">
            Publish a founder letter
          </Title>
          <Stack gap="sm">
            <Group grow>
              <TextInput
                label="Slug"
                description="lowercase-with-dashes; used in /ripples/<slug>"
                value={slug}
                onChange={(e) => setSlug(e.currentTarget.value)}
                required
              />
              <TextInput
                label="Label"
                description="Short tag e.g. Q1 2026"
                value={label}
                onChange={(e) => setLabel(e.currentTarget.value)}
                required
              />
            </Group>
            <TextInput
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.currentTarget.value)}
              required
            />
            <Group grow>
              <TextInput
                label="Author"
                value={author}
                onChange={(e) => setAuthor(e.currentTarget.value)}
                required
              />
              <Select
                label="Audience"
                data={[
                  { value: 'all', label: 'Public (all)' },
                  { value: 'artists', label: 'Artists' },
                  { value: 'hosts', label: 'Hosts' },
                  { value: 'founding_cohort', label: 'Founding cohort' },
                ]}
                value={audience}
                onChange={(v) => setAudience(v ?? 'all')}
              />
            </Group>
            <Textarea
              label="Excerpt"
              description="At least 20 characters. Shown in the index card."
              value={excerpt}
              onChange={(e) => setExcerpt(e.currentTarget.value)}
              minRows={3}
              required
            />
            <Textarea
              label="Body"
              description="At least 40 characters. Plain text or simple paragraph markdown."
              value={body}
              onChange={(e) => setBody(e.currentTarget.value)}
              minRows={8}
              required
            />
            <Group justify="flex-end">
              <Button onClick={() => publish.mutate()} loading={publish.isPending} disabled={!canPublish}>
                Publish
              </Button>
            </Group>
            {publish.isError ? (
              <Text c="red" size="sm">
                Could not publish. See worker logs.
              </Text>
            ) : null}
            {publish.isSuccess ? (
              <Text c="tide" size="sm">
                Published.
              </Text>
            ) : null}
          </Stack>
        </Card>

        <Card withBorder padding="md">
          <Title order={4} mb="md">
            Published
          </Title>
          {isLoading ? (
            <Loader size="sm" />
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Published</Table.Th>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Audience</Table.Th>
                  <Table.Th>Slug</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(data?.items ?? []).map((l) => (
                  <Table.Tr key={l.id}>
                    <Table.Td>{new Date(l.published_at).toLocaleDateString()}</Table.Td>
                    <Table.Td>{l.title}</Table.Td>
                    <Table.Td>{l.audience}</Table.Td>
                    <Table.Td>
                      <Anchor href={`/ripples/${l.slug}`} target="_blank" rel="noopener">
                        {l.slug}
                      </Anchor>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {(data?.items.length ?? 0) === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={4}>
                      <Text c="dimmed">No letters published yet.</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : null}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Stack>
    </AdminShell>
  );
}
